import { useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { BookingsScreen, BookingFormScreen } from '@screens/BookingsScreen';
import { PlannerScreen } from '@screens/PlannerScreen';
import { ReportsScreen } from '@screens/ReportsScreen';
import { AdminScreen } from '@screens/AdminScreen';
import { PlannerMetricsReportScreen } from '@screens/PlannerMetricsReportScreen';
import { SpaceCardDemo } from '@/demos';
import { ROUTES } from './routeConstants';
import { DefaultRedirect } from './DefaultRedirect';
import { EventRedirect } from './EventRedirect';
import { PersonaGuard } from './guards';
import { telemetry } from '@services/telemetry';
import { TELEMETRY_COMPONENT, EVENT_TYPES } from '@/constants';
import { scrollAppToTop } from '@utils/scrollToTop';
import { useFocusMainHeadingOnRouteChange } from '@hooks/useFocusMainHeadingOnRouteChange';

export const AppRoutes = () => {
  const location = useLocation();

  useFocusMainHeadingOnRouteChange();

  useEffect(() => {
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.NAVIGATION, {
      path: location.pathname,
    });
    // Every screen change should land at the top of the new screen.
    // The scrollable container is `<main id="app-main">`, not the window,
    // so we route through the centralized helper instead of `window.scrollTo`.
    scrollAppToTop();
  }, [location.pathname]);

  return (
    <Routes>
      <Route path={ROUTES.ROOT} element={<DefaultRedirect />} />
      <Route
        path={ROUTES.HOME}
        element={
          <PersonaGuard
            allowedPersonas={['Admin', 'Planner', 'Employee']}
            fallback={<DefaultRedirect />}
          >
            <BookingsScreen />
          </PersonaGuard>
        }
      />
      <Route
        path={ROUTES.BOOKINGS_NEW}
        element={
          <PersonaGuard
            allowedPersonas={['Admin', 'Planner', 'Employee']}
            fallback={<DefaultRedirect />}
          >
            <BookingFormScreen />
          </PersonaGuard>
        }
      />
      <Route
        path={ROUTES.BOOKINGS}
        element={
          <PersonaGuard
            allowedPersonas={['Admin', 'Planner', 'Employee']}
            fallback={<DefaultRedirect />}
          >
            <BookingsScreen />
          </PersonaGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN}
        element={
          <PersonaGuard allowedPersonas={['Admin']} fallback={<DefaultRedirect />}>
            <AdminScreen />
          </PersonaGuard>
        }
      />
      <Route
        path={ROUTES.PLANNER_METRICS_REPORT}
        element={
          <PersonaGuard allowedPersonas={['Admin', 'Planner']} fallback={<DefaultRedirect />}>
            <PlannerMetricsReportScreen />
          </PersonaGuard>
        }
      />
      <Route
        path={`${ROUTES.PLANNER}/*`}
        element={
          <PersonaGuard allowedPersonas={['Admin', 'Planner']} fallback={<DefaultRedirect />}>
            <PlannerScreen />
          </PersonaGuard>
        }
      />
      <Route
        path={`${ROUTES.REPORTS}/*`}
        element={
          <PersonaGuard allowedPersonas={['Admin', 'Planner']} fallback={<DefaultRedirect />}>
            <ReportsScreen />
          </PersonaGuard>
        }
      />
      <Route
        path={ROUTES.CALENDAR}
        element={<Navigate to={ROUTES.PLANNER_CALENDAR_VIEW} replace />}
      />
      <Route path={ROUTES.DEMO} element={<SpaceCardDemo />} />
      <Route path={ROUTES.EVENT_VIEW} element={<EventRedirect />} />
      <Route path={ROUTES.EVENT_CANCEL} element={<EventRedirect action="cancel" />} />
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
};

AppRoutes.displayName = 'AppRoutes';
